import { useAui, useAuiState } from '@assistant-ui/react';
import { useSessionStorage } from '@mantine/hooks';
import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'atlas-last-thread';

/**
 * Persists the active thread ID to sessionStorage and restores it on load.
 * Must be called from within AssistantRuntimeProvider context.
 *
 * Two seams, each wiring an external system rather than deriving render state:
 *
 * - **restore** — a one-shot fired when the runtime first finishes loading:
 *   if a thread was stored and we are sitting on the empty draft thread,
 *   switch to it. Guarded by a ref so it runs exactly once.
 * - **persist** — a `threadListItem.switchedTo` subscription: store the thread
 *   the user lands on, or clear the stored value when they switch to the draft
 *   (a fresh session). Gated until restore settles so the draft's own initial
 *   activation cannot wipe the value we are about to restore.
 */
export function useLastActiveThread() {
  const aui = useAui();
  const isLoading = useAuiState(({ threads }) => threads.isLoading);
  const [lastThreadId, setLastThreadId] = useSessionStorage<string | null>({
    key: STORAGE_KEY,
    defaultValue: null,
    getInitialValueInEffect: false,
  });
  const restoreSettled = useRef(false);

  useEffect(() => {
    if (restoreSettled.current || isLoading) {
      return;
    }
    restoreSettled.current = true;
    const { mainThreadId, newThreadId } = aui.threads().getState();
    // Only restore when we are on the empty draft — never yank the user off a
    // thread the runtime already opened.
    if (lastThreadId && mainThreadId === newThreadId) {
      // `switchToThread` throws ("Thread not found") when the stored id no
      // longer resolves — a deleted thread, or a draft that was abandoned
      // before it ever persisted. Drop the stale id and stay on the draft
      // rather than letting the throw surface as a page error.
      try {
        aui.threads().switchToThread(lastThreadId);
      } catch {
        setLastThreadId(null);
      }
    }
  }, [aui, isLoading, lastThreadId, setLastThreadId]);

  useEffect(
    () =>
      aui.on({ scope: '*', event: 'threadListItem.switchedTo' }, ({ threadId }) => {
        if (!restoreSettled.current) {
          return;
        }
        const { newThreadId } = aui.threads().getState();
        setLastThreadId(threadId === newThreadId ? null : threadId);
      }),
    [aui, setLastThreadId],
  );
}
